import React, { useEffect, useMemo, useState } from "react";
import {
  Download, Loader2, Inbox, Mail, Phone, MapPin, Building2,
  GraduationCap, Briefcase, Sparkles, RefreshCw, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const TYPE_TONE = {
  school: "#FF8C00",
  partner: "#00FFD0",
  proposal: "#FF5A00",
  general: "#A1A1AA",
};

const TYPE_LABEL = {
  school: "School",
  partner: "Partner",
  proposal: "Proposal",
  general: "General",
};

const FILTERS = [
  { id: "", label: "All" },
  { id: "school", label: "School" },
  { id: "partner", label: "Partner" },
  { id: "proposal", label: "Proposal" },
  { id: "general", label: "General" },
];

function formatDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = async (typeFilter) => {
    setLoading(true);
    try {
      const params = typeFilter ? { lead_type: typeFilter, limit: 500 } : { limit: 500 };
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const counts = useMemo(() => {
    const total = leads.length;
    const byType = leads.reduce((acc, l) => {
      acc[l.lead_type] = (acc[l.lead_type] || 0) + 1;
      return acc;
    }, {});
    return { total, byType };
  }, [leads]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params = filter ? { lead_type: filter } : {};
      const res = await api.get("/leads/export.csv", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `perforange-leads-${filter || "all"}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${counts.total} lead${counts.total === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="admin-leads-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00]">Mission Control</p>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white">Leads</h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">
            {counts.total} submission{counts.total === 1 ? "" : "s"}
            {filter ? ` · filtered to ${TYPE_LABEL[filter]?.toLowerCase()}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(filter)}
            data-testid="leads-refresh"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm font-bold text-[#D4D4D8] hover:bg-white/10 hover:text-white transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={downloadCsv}
            disabled={downloading || counts.total === 0}
            data-testid="leads-export-csv"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-sm font-black uppercase tracking-widest hover:bg-[#FFA333] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {downloading ? "Building…" : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="leads-stat-tiles">
        {FILTERS.filter((f) => f.id).map((f) => {
          const tone = TYPE_TONE[f.id];
          const n = counts.byType[f.id] || 0;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(active ? "" : f.id)}
              data-testid={`leads-tile-${f.id}`}
              className={`text-left rounded-xl border p-4 transition-all ${
                active
                  ? "bg-[#FF8C00]/10 border-[#FF8C00]/60"
                  : "bg-[#0C0C10] border-white/8 hover:border-white/20"
              }`}
            >
              <p className="text-[10px] font-mono-tech uppercase tracking-[0.22em]" style={{ color: tone }}>
                {f.label}
              </p>
              <p className="mt-2 font-display font-black text-2xl md:text-3xl text-white">{n}</p>
            </button>
          );
        })}
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="leads-filter-pills">
        {FILTERS.map((f) => (
          <button
            key={f.id || "all"}
            onClick={() => setFilter(f.id)}
            data-testid={`leads-filter-${f.id || "all"}`}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${
              filter === f.id
                ? "bg-[#FF8C00] text-[#0A0A0C]"
                : "bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Body */}
      {loading ? (
        <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-16 flex items-center justify-center text-[#A1A1AA]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads…
        </div>
      ) : leads.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-12 text-center">
          <Inbox className="w-10 h-10 text-[#6a6a70] mx-auto mb-3" />
          <p className="text-[#A1A1AA]">No leads here yet.</p>
          <p className="mt-1 text-xs text-[#6a6a70]">New submissions from /contact and /partnership will land in this view.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/8 bg-[#0C0C10] overflow-hidden" data-testid="admin-leads-table">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/8 text-[10px] font-mono-tech uppercase tracking-widest text-[#6a6a70]">
            <div className="col-span-2">Received</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-3">Contact</div>
            <div className="col-span-3">Organization</div>
            <div className="col-span-2 text-right">Detail</div>
          </div>
          <div className="divide-y divide-white/8">
            {leads.map((l) => {
              const tone = TYPE_TONE[l.lead_type] || "#A1A1AA";
              const isOpen = expanded === l.id;
              return (
                <div key={l.id} data-testid={`lead-row-${l.id}`}>
                  <div
                    onClick={() => setExpanded(isOpen ? null : l.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded(isOpen ? null : l.id);
                      }
                    }}
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="md:col-span-2">
                      <p className="text-sm text-white">{formatDate(l.created_at)}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span
                        className="text-[10px] font-mono-tech font-black uppercase tracking-widest px-2 py-1 rounded-md inline-block"
                        style={{ background: `${tone}1A`, border: `1px solid ${tone}55`, color: tone }}
                      >
                        {TYPE_LABEL[l.lead_type] || l.lead_type}
                      </span>
                    </div>
                    <div className="md:col-span-3 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{l.full_name}</p>
                      <a
                        href={`mailto:${l.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-[#FF8C00] hover:underline truncate inline-block max-w-full"
                      >
                        {l.email}
                      </a>
                    </div>
                    <div className="md:col-span-3 min-w-0">
                      <p className="text-sm text-[#D4D4D8] truncate">{l.organization || "—"}</p>
                      <p className="text-xs text-[#6a6a70] truncate">
                        {[l.role, l.region].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                    <div className="md:col-span-2 flex items-center justify-end gap-2 text-[#A1A1AA]">
                      <span className="hidden md:inline text-[10px] font-mono-tech uppercase tracking-widest">
                        {isOpen ? "Collapse" : "Expand"}
                      </span>
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                  {isOpen && (
                    <div
                      data-testid={`lead-detail-${l.id}`}
                      className="px-5 pb-6 pt-1 grid grid-cols-1 md:grid-cols-2 gap-5 bg-[#0A0A0C] border-t border-white/5"
                    >
                      <div className="space-y-2.5 text-sm">
                        <Row icon={Mail} label="Email" value={<a href={`mailto:${l.email}`} className="text-[#FF8C00] hover:underline">{l.email}</a>} />
                        <Row icon={Phone} label="Phone" value={l.phone ? <a href={`tel:${l.phone}`} className="text-[#FF8C00] hover:underline">{l.phone}</a> : "—"} />
                        <Row icon={MapPin} label="Region" value={l.region || "—"} />
                        <Row icon={Briefcase} label="Role" value={l.role || "—"} />
                        <Row icon={Building2} label="Organization" value={l.organization || "—"} />
                        <Row icon={GraduationCap} label="Volume" value={l.student_volume || "—"} />
                        <Row icon={Sparkles} label="Hardware" value={l.hardware_status || "—"} />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-[#FF8C00] mb-2">Message</p>
                        <div className="rounded-lg border border-white/8 bg-[#0C0C10] p-4 text-sm text-[#D4D4D8] whitespace-pre-wrap break-words min-h-[120px]">
                          {l.message || <span className="text-[#6a6a70] italic">No message provided.</span>}
                        </div>
                        <p className="mt-3 text-[10px] font-mono-tech uppercase tracking-widest text-[#6a6a70]">
                          ID: {l.id}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-md bg-[#FF8C00]/8 border border-[#FF8C00]/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#FF8C00]" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-mono-tech uppercase tracking-widest text-[#6a6a70]">{label}</p>
        <div className="text-sm text-white break-words">{value}</div>
      </div>
    </div>
  );
}
